/**
 * OUTFIT — Dashboard Commissions
 * Trace : clics partenaires, achats confirmés, commissions calculées
 * Sources :
 *  - Clics : AsyncStorage (local) via PurchaseTrackingService
 *  - Achats : API Lambda /purchases
 *  - Commissions : calculées selon les taux partenaires
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity,
  StyleSheet, SafeAreaView, ActivityIndicator, RefreshControl,
} from 'react-native';
import ThemedRefreshControl from '../components/ThemedRefreshControl';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { colors, spacing, radius, shadow } from '../theme';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { AWS_CONFIG } from '../config/aws_config';

const API = AWS_CONFIG.apiUrl;

// Taux de commission partenaires (estimés / contractuels)
// Taux Skimlinks actuel : ~4.5% moyen sur mode
// Taux AWIN après approbation : 5-8%
const PARTNER_RATES = {
  zalando: { rate: 0.045, label: 'Zalando', color: '#FF6900', status: 'skimlinks', statusLabel: '✅ Skimlinks actif (4.5%)' },
  asos:    { rate: 0.045, label: 'ASOS',    color: '#111',    status: 'skimlinks', statusLabel: '✅ Skimlinks actif (4.5%)' },
  amazon:  { rate: 0.045, label: 'Amazon',  color: '#FF9900', status: 'skimlinks', statusLabel: '✅ Skimlinks actif (4.5%)' },
  hm:      { rate: 0.045, label: 'H&M',     color: '#E50010', status: 'skimlinks', statusLabel: '✅ Skimlinks actif (4.5%)' },
  zara:    { rate: 0.045, label: 'Zara',    color: '#000',    status: 'skimlinks', statusLabel: '✅ Skimlinks actif (4.5%)' },
};

export default function DashboardScreen({ navigation }) {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [purchases,   setPurchases]   = useState([]);
  const [clicks,      setClicks]      = useState([]);
  const [period,      setPeriod]      = useState('month'); // week | month | all

  const load = useCallback(async () => {
    try {
      // 1. Charger les achats confirmés depuis Lambda
      if (user?.accessToken) {
        try {
          const res = await fetch(`${API}/purchases`, {
            headers: { Authorization: `Bearer ${user.accessToken}` },
          });
          if (res.ok) {
            const data = await res.json();
            setPurchases(Array.isArray(data) ? data : []);
          }
        } catch(e) {}
      }

      // 2. Charger les clics depuis AsyncStorage
      try {
        const raw = await AsyncStorage.getItem('outfit_click_history');
        setClicks(raw ? JSON.parse(raw) : []);
      } catch(e) {}

    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const onRefresh = useCallback(() => { setRefreshing(true); load(); }, [load]);

  // Filtrer par période
  const now = Date.now();
  const PERIODS = { week: 7, month: 30, all: 9999 };
  const days = PERIODS[period];
  const cutoff = now - days * 24 * 60 * 60 * 1000;

  const filteredPurchases = purchases.filter(p => new Date(p.purchased_at || p.created_at || 0).getTime() > cutoff);
  const filteredClicks    = clicks.filter(c => (c.ts || 0) > cutoff);

  // Calculs
  const totalRevenue   = filteredPurchases.reduce((s, p) => s + (p.price || 0), 0);
  const partnerComm    = filteredPurchases.reduce((s, p) => {
    const rate = (PARTNER_RATES[p.partner] || {}).rate || 0;
    return s + (p.price || 0) * rate;
  }, 0);
  const partnerCommRound = Math.round(partnerComm * 100) / 100;

  // Agrégats par partenaire
  const byPartner = {};
  filteredPurchases.forEach(p => {
    const key = p.partner || 'unknown';
    if (!byPartner[key]) byPartner[key] = { count: 0, revenue: 0, commission: 0 };
    byPartner[key].count++;
    byPartner[key].revenue   += (p.price || 0);
    byPartner[key].commission += (p.price || 0) * ((PARTNER_RATES[key] || {}).rate || 0);
  });

  // Clics par partenaire
  const clicksByPartner = {};
  filteredClicks.forEach(c => {
    const key = c.partner || 'unknown';
    clicksByPartner[key] = (clicksByPartner[key] || 0) + 1;
  });

  const totalClicks = filteredClicks.length;
  const convRate    = totalClicks > 0 ? Math.round((filteredPurchases.length / totalClicks) * 100) : 0;

  if (loading) return (
    <SafeAreaView style={s.safe}>
      <ActivityIndicator style={{ flex: 1 }} color={theme.text} />
    </SafeAreaView>
  );

  return (
    <SafeAreaView style={s.safe}>
      <View style={s.headerPrem}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={s.backBtnPrem}>
          <Text style={s.backTxtPrem}>‹</Text>
        </TouchableOpacity>
        <View style={{ flex: 1, alignItems: 'center' }}>
          <Text style={s.headerBadgePrem}>📊 ANALYTICS</Text>
          <Text style={s.headerTitlePrem}>Tableau de bord</Text>
        </View>
        <View style={{ width: 40 }} />
      </View>

      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: 40 }}
        refreshControl={<ThemedRefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      >
        {/* Période */}
        <View style={s.periodRow}>
          {[['week','7 jours'],['month','30 jours'],['all','Tout']].map(([k,l]) => (
            <TouchableOpacity key={k} style={[s.periodBtn, period === k && s.periodActive]}
              onPress={() => setPeriod(k)}>
              <Text style={[s.periodLabel, period === k && s.periodLabelActive]}>{l}</Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* KPIs principaux */}
        <View style={s.kpiGrid}>
          <View style={[s.kpiCard, shadow.sm]}>
            <Text style={s.kpiNum}>{filteredPurchases.length}</Text>
            <Text style={s.kpiLabel}>Achats confirmés</Text>
          </View>
          <View style={[s.kpiCard, shadow.sm]}>
            <Text style={s.kpiNum}>{totalRevenue.toFixed(0)} €</Text>
            <Text style={s.kpiLabel}>Volume total</Text>
          </View>
          <View style={[s.kpiCard, shadow.sm]}>
            <Text style={[s.kpiNum, { color: '#1565C0' }]}>{partnerCommRound.toFixed(2)} €</Text>
            <Text style={s.kpiLabel}>Comm. partenaires estimée</Text>
          </View>
        </View>

        {/* Funnel */}
        <View style={s.section}>
          <Text style={s.sectionLabel}>FUNNEL</Text>
          <View style={[s.funnelCard, shadow.sm]}>
            {[
              { label: 'Clics partenaires',  val: totalClicks,               color: '#1565C0' },
              { label: 'Achats confirmés',   val: filteredPurchases.length,  color: '#2E7D32' },
              { label: 'Taux conversion',    val: convRate + '%',            color: '#6A1B9A' },
            ].map((row, i) => (
              <View key={i} style={[s.funnelRow, i > 0 && s.funnelRowBorder]}>
                <Text style={s.funnelLabel}>{row.label}</Text>
                <Text style={[s.funnelVal, { color: row.color }]}>{row.val}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Par partenaire — achats */}
        <View style={s.section}>
          <Text style={s.sectionLabel}>PAR PARTENAIRE</Text>
          {Object.entries(PARTNER_RATES).map(([key, info]) => {
            const stats   = byPartner[key] || { count: 0, revenue: 0, commission: 0 };
            const clics   = clicksByPartner[key] || 0;
            return (
              <View key={key} style={[s.partnerRow, shadow.sm]}>
                <View style={[s.partnerDot, { backgroundColor: info.color }]} />
                <View style={s.partnerInfo}>
                  <View style={s.partnerTop}>
                    <Text style={s.partnerName}>{info.label}</Text>
                    <Text style={s.partnerStatus}>{info.statusLabel}</Text>
                  </View>
                  <View style={s.partnerStats}>
                    <Text style={s.partnerStat}>{clics} clics</Text>
                    <Text style={s.partnerStat}>·</Text>
                    <Text style={s.partnerStat}>{stats.count} achats</Text>
                    <Text style={s.partnerStat}>·</Text>
                    <Text style={s.partnerStat}>{stats.revenue.toFixed(0)} € vol.</Text>
                    <Text style={s.partnerStat}>·</Text>
                    <Text style={[s.partnerStat, { color: '#2E7D32', fontWeight: '600' }]}>
                      {stats.commission.toFixed(2)} € comm.
                    </Text>
                  </View>
                  <View style={s.rateBar}>
                    <Text style={s.rateLabel}>Taux : {(info.rate * 100).toFixed(0)}%</Text>
                    <View style={s.rateTrack}>
                      <View style={[s.rateFill, { width: (info.rate / 0.1 * 100) + '%', backgroundColor: info.color }]} />
                    </View>
                  </View>
                </View>
              </View>
            );
          })}
        </View>

        {/* Actions à faire */}
        <View style={s.section}>
          <Text style={s.sectionLabel}>ACTIONS REQUISES</Text>
          {[
            { icon:'✅', title:'Skimlinks actif (ID 301454X1789418)', desc:'Toutes les URLs sont trackées automatiquement — commissions sur 60 000 marchands dont Zalando, ASOS, H&M, Amazon', done: true, test: true },
            { icon:'🔗', title:'Approuver Zalando sur AWIN',  desc:'ui.awin.com → MID 7476 → pour maximiser la commission (6% vs 4.5% Skimlinks)', done: false },
            { icon:'🔗', title:'Approuver ASOS sur AWIN',     desc:'ui.awin.com → MID 11949 → pour maximiser la commission (5% vs 4.5% Skimlinks)', done: false },
            { icon:'📦', title:"S'inscrire Amazon Partenaires", desc:"partenaires.amazon.fr → pour commission dédiée (3-10% vs 4.5% Skimlinks)", done: false },
          ].map((a, i) => (
            <TouchableOpacity key={i} style={[s.actionCard, shadow.sm]}
              onPress={() => a.test && navigation.navigate('SkimlinksTest')}
              activeOpacity={a.test ? 0.7 : 1}>
              <Text style={s.actionIcon}>{a.icon}</Text>
              <View style={s.actionInfo}>
                <Text style={s.actionTitle}>{a.title}</Text>
                <Text style={s.actionDesc}>{a.desc}</Text>
                {a.test && <Text style={s.testLink}>Tester →</Text>}
              </View>
              <View style={[s.actionDot, { backgroundColor: a.done ? '#2E7D32' : '#FF6900' }]} />
            </TouchableOpacity>
          ))}
        </View>

        {/* Achats récents */}
        {filteredPurchases.length > 0 && (
          <View style={s.section}>
            <Text style={s.sectionLabel}>ACHATS RÉCENTS</Text>
            {filteredPurchases.slice(0, 10).map((p, i) => {
              const info = PARTNER_RATES[p.partner] || {};
              const comm = ((p.price || 0) * (info.rate || 0)).toFixed(2);
              const date = new Date(p.purchased_at || p.created_at).toLocaleDateString('fr-FR');
              return (
                <View key={i} style={[s.purchaseRow, shadow.sm]}>
                  <View style={[s.purchaseDot, { backgroundColor: info.color || '#999' }]} />
                  <View style={s.purchaseInfo}>
                    <Text style={s.purchaseName} numberOfLines={1}>{p.product_name || p.name}</Text>
                    <Text style={s.purchaseMeta}>{info.label || p.partner} · {date}</Text>
                  </View>
                  <View style={s.purchaseRight}>
                    <Text style={s.purchasePrice}>{(p.price || 0).toFixed(0)} €</Text>
                    <Text style={s.purchaseComm}>+{comm} €</Text>
                  </View>
                </View>
              );
            })}
          </View>
        )}

        {filteredPurchases.length === 0 && (
          <View style={s.emptyBlock}>
            <Text style={s.emptyIcon}>📊</Text>
            <Text style={s.emptyTitle}>Pas encore de données</Text>
            <Text style={s.emptyDesc}>Les achats confirmés par les utilisateurs apparaîtront ici avec les commissions calculées.</Text>
          </View>
        )}

      </ScrollView>
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  safe:           { flex:1, backgroundColor:'#FFFFFF' },
  header:         { flexDirection:'row', justifyContent:'space-between', alignItems:'center', paddingHorizontal:spacing.xl, paddingVertical:spacing.md },
  back:           { fontSize:22, color:colors.text.primary, fontWeight:'300' },
  title:          { fontSize:11, letterSpacing:4, fontWeight:'600', color:colors.text.primary },
  divider:        { height:1, backgroundColor:colors.bg.border },

  periodRow:      { flexDirection:'row', gap:8, paddingHorizontal:spacing.xl, paddingTop:spacing.lg, paddingBottom:spacing.md },
  periodBtn:      { paddingHorizontal:16, paddingVertical:8, borderRadius:radius.pill, borderWidth:1.5, borderColor:colors.bg.border },
  periodActive:   { borderColor:colors.text.primary, backgroundColor:colors.text.primary },
  periodLabel:    { fontSize:12, fontWeight:'500', color:colors.text.secondary },
  periodLabelActive: { color:'#fff' },

  kpiGrid:        { flexDirection:'row', flexWrap:'wrap', paddingHorizontal:spacing.xl, gap:10, marginBottom:spacing.lg },
  kpiCard:        { width:'47%', backgroundColor:'#FFFFFF', borderRadius:radius.md, borderWidth:1, borderColor:colors.bg.border, padding:spacing.md },
  kpiNum:         { fontSize:22, fontWeight:'300', color:colors.text.primary, marginBottom:4 },
  kpiLabel:       { fontSize:9, letterSpacing:1, color:colors.text.faint, textTransform:'uppercase' },

  section:        { paddingHorizontal:spacing.xl, marginBottom:spacing.lg },
  sectionLabel:   { fontSize:9, letterSpacing:3, fontWeight:'600', color:colors.text.faint, textTransform:'uppercase', marginBottom:spacing.sm },

  funnelCard:     { backgroundColor:'#FFFFFF', borderRadius:radius.md, borderWidth:1, borderColor:colors.bg.border, overflow:'hidden' },
  funnelRow:      { flexDirection:'row', justifyContent:'space-between', alignItems:'center', padding:spacing.md },
  funnelRowBorder:{ borderTopWidth:1, borderTopColor:colors.bg.border },
  funnelLabel:    { fontSize:13, color:colors.text.secondary },
  funnelVal:      { fontSize:18, fontWeight:'300' },

  partnerRow:     { flexDirection:'row', alignItems:'flex-start', backgroundColor:'#FFFFFF', borderRadius:radius.md, borderWidth:1, borderColor:colors.bg.border, padding:spacing.md, marginBottom:8, gap:12 },
  partnerDot:     { width:10, height:10, borderRadius:5, marginTop:4 },
  partnerInfo:    { flex:1 },
  partnerTop:     { flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:4 },
  partnerName:    { fontSize:14, fontWeight:'500', color:colors.text.primary },
  partnerStatus:  { fontSize:10, color:colors.text.faint },
  partnerStats:   { flexDirection:'row', flexWrap:'wrap', gap:4, marginBottom:6 },
  partnerStat:    { fontSize:11, color:colors.text.faint },
  rateBar:        { flexDirection:'row', alignItems:'center', gap:8 },
  rateLabel:      { fontSize:10, color:colors.text.faint, width:60 },
  rateTrack:      { flex:1, height:4, backgroundColor:colors.bg.surface, borderRadius:2, overflow:'hidden' },
  rateFill:       { height:'100%', borderRadius:2 },

  actionCard:     { flexDirection:'row', alignItems:'center', backgroundColor:'#FFFFFF', borderRadius:radius.md, borderWidth:1, borderColor:colors.bg.border, padding:spacing.md, marginBottom:8, gap:12 },
  actionIcon:     { fontSize:20 },
  actionInfo:     { flex:1 },
  actionTitle:    { fontSize:13, fontWeight:'500', color:colors.text.primary, marginBottom:2 },
  actionDesc:     { fontSize:11, color:colors.text.faint, lineHeight:16 },
  actionDot:      { width:10, height:10, borderRadius:5 },
  testLink:       { fontSize:11, color:'#1565C0', fontWeight:'500', marginTop:4 },

  purchaseRow:    { flexDirection:'row', alignItems:'center', backgroundColor:'#FFFFFF', borderRadius:radius.md, borderWidth:1, borderColor:colors.bg.border, padding:spacing.md, marginBottom:8, gap:12 },
  purchaseDot:    { width:8, height:8, borderRadius:4 },
  purchaseInfo:   { flex:1 },
  purchaseName:   { fontSize:13, fontWeight:'500', color:colors.text.primary },
  purchaseMeta:   { fontSize:10, color:colors.text.faint, marginTop:2 },
  purchaseRight:  { alignItems:'flex-end' },
  purchasePrice:  { fontSize:14, fontWeight:'500', color:colors.text.primary },
  purchaseComm:   { fontSize:11, color:'#2E7D32', fontWeight:'500' },

  emptyBlock:     { padding:spacing.xl*2, alignItems:'center', gap:8 },
  emptyIcon:      { fontSize:40 },
  emptyTitle:     { fontSize:16, fontWeight:'500', color:colors.text.primary },
  emptyDesc:      { fontSize:12, color:colors.text.faint, textAlign:'center', lineHeight:18 },

  // ═══ Style premium ═══
  headerPrem: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingVertical: 14, backgroundColor: '#FAFAFA',
    borderBottomWidth: 0.5, borderBottomColor: '#E8E0C8',
  },
  backBtnPrem:     { width: 40, height: 40, alignItems: 'center', justifyContent: 'center' },
  backTxtPrem:     { fontSize: 32, color: '#1A1A1A', fontWeight: '300' },
  headerBadgePrem: { fontSize: 9, letterSpacing: 2.5, color: '#B8860B', fontWeight: '700', marginBottom: 4 },
  headerTitlePrem: { fontSize: 18, fontWeight: '300', color: '#1A1A1A', letterSpacing: 0.3 },
});
